import CloseIcon from '@mui/icons-material/Close';
import { ListItemButton, List, Button, Grid, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import { useState, FC, useEffect, useCallback } from 'react';
import {
  Item,
  WsColors,
  WsIconText,
  WsTypoVariants,
  WsDialog,
  WsTypography,
  WsButton,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import conversationWidgetStore from '../store/conversationWidgetStore';
import { actionListForCrusible } from '../utils/conversationScrollList';

type ColorType = {
  selectedIndex: boolean;
};

const StyledButton = styled(Button)(({ selectedIndex }: ColorType) => ({
  padding: '18px',
  marginLeft: '10px',
  marginRight: '10px',
  justifyContent: 'flex-start',
  backgroundColor: selectedIndex ? WsColors.TheBlues500 : WsColors.White,
  color: selectedIndex ? WsColors.White : WsColors.TertiaryShades900,
  '&:hover': {
    backgroundColor: selectedIndex ? WsColors.TheBlues500 : WsColors.White,
    color: selectedIndex ? WsColors.White : WsColors.TertiaryShades900,
  },
}));

interface IWidgetDrawerNavigation {
  getTabIndex(args: number): void;
  defaultValue?: number;
  closeDrawer(): void;
  isMapped: boolean;
}

const WidgetDrawerNavigationNew: FC<IWidgetDrawerNavigation> = ({
  getTabIndex,
  defaultValue = 0,
  closeDrawer,
  isMapped,
}) => {
  const selectedJob = conversationWidgetStore((state) => state.selectedJob);

  const workspace = useWorkspaceContext();
  const crucibleList = actionListForCrusible(isMapped);
  const [isTempConvSet, setIsTempConvSet] = useState(false);
  const [isPopupLoaded, setIsPopupLoaded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [tempIndex, setTempIndex] = useState<number | null>(null);
  const view = useConversationWidgetStore((state) => state.view);
  const isMappingFormChanged = useConversationWidgetStore((state) => state.isMappingFormChanged);
  const tempConv = useConversationWidgetStore((state) => state.tempConv);
  const mapBeforeNavigation = useConversationWidgetStore((state) => state.mapBeforeNavigation);
  const openModal = useConversationWidgetStore((state) => state.setCandidate);
  const setTempConv = useConversationWidgetStore((state) => state.setTempConv);
  const setChangeJob = useConversationWidgetStore((state) => state.setChangeJob);
  const changeJob = useConversationWidgetStore((state) => state.changeJob);
  const setMapBeforeNavigation = useConversationWidgetStore(
    (state) => state.setMapBeforeNavigation
  );
  const setIsMappingFormChanged = useConversationWidgetStore(
    (state) => state.setIsMappingFormChanged
  );
  useEffect(() => {
    defaultValue && setSelectedIndex(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (tempConv && tempIndex != null && isTempConvSet) {
      setIsTempConvSet(false);
      setSelectedIndex(tempIndex);
      getTabIndex(tempIndex);
      setTempIndex(null);
    } else if (tempConv) {
      setIsTempConvSet(true);
    }
  }, [getTabIndex, isTempConvSet, tempConv, tempIndex]);

  const handleListItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number
  ) => {
    if (
      selectedIndex == 0 &&
      index != 0 &&
      isMappingFormChanged &&
      (!isPopupLoaded || isEmpty(tempConv))
    ) {
      setTempIndex(index);
      setOpenDialog(true);
      setIsMappingFormChanged(false);
      setIsPopupLoaded(true);
    } else if (tempIndex == null) {
      setSelectedIndex(index);
      getTabIndex(index);
    }
  };

  const handleClose = useCallback(() => {
    if (mapBeforeNavigation && tempConv) {
      setMapBeforeNavigation(false);
      setTempConv(null);
      const recordedConversation = tempConv;
      closeDrawer();
      openModal(recordedConversation);
    } else {
      closeDrawer();
    }
  }, [closeDrawer, mapBeforeNavigation, openModal, setMapBeforeNavigation, setTempConv, tempConv]);

  useEffect(() => {
    //  setChangeJob(false);
    changeJob && handleClose();
  }, [changeJob, handleClose, setChangeJob]);

  const handleDialog = () => {
    if (tempIndex == 0 || tempIndex) {
      setSelectedIndex(tempIndex);
      getTabIndex(tempIndex);
      setTempIndex(null);
      setOpenDialog(false);
    }
  };

  const handleMapOrUpdate = () => {
    setOpenDialog(false);
    setMapBeforeNavigation(true);
  };

  return (
    <>
      <Item>
        <WsIconText
          icon={<CloseIcon id="widgetDrawer" onClick={handleClose} />}
          text="Actions"
          textSize={WsTypoVariants.SUB_TITLE}
          onClick={handleClose}
        />
      </Item>
      <List component="nav" sx={{ mt: 2 }}>
        {crucibleList.map((list, index) => (
          <>
            {(view === 'submission' && index === 0) ||
            (index === 7 && workspace.id !== 'c982f84c-d9a8-492a-906b-34a2b1831c3e') ? (
              <></>
            ) : (
              <ListItemButton
                key={index}
                sx={{
                  padding: '16px 16px',
                  '&:hover': {
                    backgroundColor: WsColors.White,
                  },
                }}
                onClick={(event) => handleListItemClick(event, index)}
              >
                <StyledButton
                  fullWidth
                  disabled={
                    list.label === 'Call'
                      ? true
                      : list.label === 'Recruiter Questions' &&
                        isEmpty(selectedJob && selectedJob?.questions)
                      ? true
                      : false
                  }
                  variant="text"
                  selectedIndex={selectedIndex == index ? true : false}
                  startIcon={<list.icon />}
                >
                  {list.label}
                </StyledButton>
              </ListItemButton>
            )}
          </>
        ))}
      </List>
      {openDialog && (
        <WsDialog openDialog={openDialog}>
          <Grid container p={3} spacing={5} justifyContent="center">
            <Grid item xs={12}>
              <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
                {`Do you want to ${
                  isMapped ? 'Update' : 'Map'
                } the changes to the conversation before you leave?`}
              </WsTypography>
            </Grid>
            <Grid item container xs={12} justifyContent="flex-end">
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <WsButton onClick={handleMapOrUpdate} variant="contained">
                  {`${isMapped ? 'Update' : 'Map'}`}
                </WsButton>
                <WsButton
                  onClick={() => {
                    setIsMappingFormChanged(false);
                    handleDialog();
                  }}
                  variant="outlined"
                >
                  Skip
                </WsButton>
              </Stack>
            </Grid>
          </Grid>
        </WsDialog>
      )}
    </>
  );
};

export default WidgetDrawerNavigationNew;
